import { AlertTriangle } from '@untitledui/icons';
import { useMemo } from 'react';

import { EmptyState } from '@/lib/base/empty-state';
import { isTransientError } from '@/lib/errors';
import { sanitizeError } from '@/lib/utils/errors';
import { cx } from '@/utils/cx';

export interface QueryErrorStateProps {
  /** The query error to classify and render. */
  error: unknown;
  /** Human label for what failed to load (e.g. "contacts"); used as the hard-error title. */
  label: string;
  /**
   * Optional retry handler. When provided, a Retry control is shown — except
   * while `isRetrying`, when there is nothing for the user to do.
   * Must be a stable reference (wrap in useCallback) — an unstable value
   * re-creates the retry action object each render and re-renders EmptyState.
   */
  onRetry?: () => void;
  /**
   * True while a retry is actually in flight — the query's automatic retries
   * (React Query: `isFetching && failureCount > 0`) or a manual refetch the
   * user just triggered. Drives the "Retrying…" copy and hides the Retry
   * control, so the two never contradict each other: a settled error is
   * either being retried or waiting on the user, never both. Defaults to
   * false because a React Query `error` is only populated once its retries
   * are exhausted, so the usual error render has nothing in flight.
   */
  isRetrying?: boolean;
  size?: 'sm' | 'md' | 'lg';
}

export function QueryErrorState({
  error,
  label,
  onRetry,
  isRetrying = false,
  size,
}: QueryErrorStateProps) {
  const transient = isTransientError(error);

  let title: string;
  let description: string;
  if (transient) {
    title = 'Trouble connecting';
    description = isRetrying ? 'Retrying…' : 'Try again in a moment.';
  } else {
    title = label;
    description = sanitizeError(error);
  }

  const action = useMemo(
    () =>
      onRetry && !isRetrying ? { label: 'Retry', onClick: onRetry } : undefined,
    [onRetry, isRetrying]
  );

  return (
    <EmptyState
      variant='noData'
      size={size ?? 'md'}
      title={title}
      description={description}
      // EmptyState ignores `iconColor` when a custom `icon` is supplied, so the
      // severity color is applied to the icon directly to keep the transient
      // (gray) vs hard-error (red) distinction visible.
      icon={
        <AlertTriangle
          className={cx(
            'size-6',
            transient ? 'text-utility-gray-600' : 'text-utility-error-600'
          )}
        />
      }
      action={action}
    />
  );
}
